import joi from 'joi';
import { TransactionType } from '@loyalty/shared-common/profile-api-client';
import {
  $channelInfo,
  $rewardType,
  $rewardsI18nextAttributes,
  RewardType,
} from '../../rewards-api-client';
import { $impactInteraction, ImpactInteraction } from '../../profile-api-client/types/interaction';
import {
  $updateTotals,
  AccessRule,
  ActionTypes,
  ImpactMode,
  ImpactRule,
  RewardListRuleState,
} from './rule.type';
import { CompleteImpactRule } from '../../event-processing-api-client';
import { localizedPropertySchema } from '../../utils';

export interface ImpactAttributes<T = ImpactRule> {
  accountId: string;
  eventId?: string;
  clientEventId?: string;
  eventTimestamp?: string;
  profileId: string;
  profileVersion: number;
  rules: T[];
  interaction?: ImpactInteraction;
}

export interface AccessImpactAttributes {
  programId: string;
  accountId?: string;
  profileId?: string;
  rule: AccessRule;
}

const $action = joi
  .object()
  .when('kind', {
    is: ActionTypes.REWARD_ATTRIBUTION,
    then: joi.object({
      impact: joi.object({
        rewardId: joi.string().uuid(),
      }),
    }),
  })
  .when('kind', {
    is: ActionTypes.CAMPAIGN_CODE,
    then: joi.object({
      impact: joi.object({
        promocode: joi.string(),
        campaignId: joi.string(),
        giftId: joi.number().optional(),
      }),
    }),
  })
  .when('kind', {
    is: ActionTypes.TRANSACTION,
    then: joi.object({
      impact: joi
        .object()
        .when('mode', {
          is: ImpactMode.FIXED_AMOUNT,
          then: joi.object({
            currency: joi.string().uuid(),
            value: joi.number(),
          }),
        })
        .when('mode', {
          is: ImpactMode.VARIABLE_AMOUNT,
          then: joi.object({
            currency: joi.string().uuid(),
            factorField: joi.string(),
            factor: joi.number(),
          }),
        }),
      updateTotals: $updateTotals,
    }),
  })
  .when('kind', {
    is: ActionTypes.STOP,
    then: joi.object({
      impact: joi.object({}),
    }),
  });

const $rewardRule = joi.object<CompleteImpactRule>({
  id: joi.string().uuid().optional().allow(null),
  accountId: joi.string().optional(),
  programId: joi.string().uuid(),
  type: joi.string().valid(...Object.values(TransactionType)),
  currency: joi
    .object({
      id: joi.string().uuid(),
      name: joi.string().optional().allow(null),
      value: joi.number(),
    })
    .optional(),
  state: joi
    .string()
    .valid(...Object.values(RewardListRuleState))
    .optional(),
  i18nAttributes: joi
    .object()
    .pattern(
      /.*/,
      joi.object({
        description: joi.string(),
      }),
    )
    .optional(),
  reward: joi
    .object({
      id: joi.string().uuid(),
      type: $rewardType.optional().default(RewardType.PROMOCODE),
      // TODO: we could split this type between rule-api and profile-api instead of this
      i18nAttributes: $rewardsI18nextAttributes,
      channelInfoByLanguage: localizedPropertySchema($channelInfo).optional(),
    })
    .optional(),
  actions: joi.array().items($action),
});

export const $impactAttributes = joi.object<ImpactAttributes>({
  accountId: joi.string(),
  eventId: joi.string().optional().allow(null),
  clientEventId: joi.string().optional().allow(null),
  eventTimestamp: joi.string().isoDate().optional(),
  profileId: joi.string().uuid(),
  profileVersion: joi.number(),
  // min(0) allows interaction-only apply-impact (no matching earn rules)
  rules: joi.array().items($rewardRule).min(0),
  interaction: $impactInteraction.optional(),
});
